import { Guid } from "client_legacy/ndaw/guid";
import { environmentHttpClient } from "../httpClient";
import { IntegrationFlowStatus } from "./integrationBuilderFlows";
import { SecretValue } from "../Apps/common";

const BASE_URL = `/UiApi/IntegrationBuilder/Authentication`;

export enum IntegrationFlowAuthenticationKind {
	ApiKey = 1,
	OAuth2 = 2,
	Basic = 3,
	ClientCertificate = 4,
	Afas = 5
};

export type IntegrationFlowAuthenticationUsageModel = {
	integrationId: number;
	versionId: Guid;
	name: string;
	status: IntegrationFlowStatus;
};

export type IntegrationFlowAuthenticationListItemModel = {
	id: number;
	name: string;
	kind: IntegrationFlowAuthenticationKind;
	usages: readonly IntegrationFlowAuthenticationUsageModel[];
};

export function getAuthenticationList(
	signal?: AbortSignal
): Promise<readonly IntegrationFlowAuthenticationListItemModel[]> {
	return environmentHttpClient.get<readonly IntegrationFlowAuthenticationListItemModel[]>(
		"UiApi/IntegrationBuilder/Authentication",
		undefined,
		{ signal }
	);
}

export enum StringSecretDiscriminator {
	String = 1,
	Secret = 2
}

export type StringViewModel = {
	kind: StringSecretDiscriminator.String;
	string: string;
};

export type SecretViewModel = {
	kind: StringSecretDiscriminator.Secret;
};

export type StringOrSecretViewModel =
	| StringViewModel
	| SecretViewModel;

export type KeyedValueViewModel = {
	id: Guid;
	key: string;
	value: StringOrSecretViewModel;
};

export type IntegrationFlowApiKeyAuthenticationViewModel = BaseIntegrationFlowAuthenticationViewModel & {
	kind: IntegrationFlowAuthenticationKind.ApiKey;
	headers: readonly KeyedValueViewModel[];
	queryParameters: readonly KeyedValueViewModel[];
};

export type IntegrationFlowOAuth2AuthenticationViewModel = BaseIntegrationFlowAuthenticationViewModel & {
	kind: IntegrationFlowAuthenticationKind.OAuth2;
	authorizationUrl: string;
	tokenUrl: string;
	clientId: string;
	scopes: readonly string[];
	redirectUrl: string;
};

export type IntegrationFlowClientCertificateAuthenticationViewModel = BaseIntegrationFlowAuthenticationViewModel & {
	kind: IntegrationFlowAuthenticationKind.ClientCertificate;
	certificateFileName: string;
};

export type IntegrationFlowAfasAuthenticationViewModel = BaseIntegrationFlowAuthenticationViewModel & {
	kind: IntegrationFlowAuthenticationKind.Afas;
	apiUrl: string;
	environmentKey: string;
};

export type IntegrationFlowBasicAuthenticationViewModel = BaseIntegrationFlowAuthenticationViewModel & {
	kind: IntegrationFlowAuthenticationKind.Basic;
};

type BaseIntegrationFlowAuthenticationViewModel = {
	name: string;
	allowedHosts: OptionalModel<readonly string[]>;
};

export type IntegrationFlowAuthenticationViewModel =
	| IntegrationFlowApiKeyAuthenticationViewModel
	| IntegrationFlowOAuth2AuthenticationViewModel
	| IntegrationFlowBasicAuthenticationViewModel
	| IntegrationFlowClientCertificateAuthenticationViewModel
	| IntegrationFlowAfasAuthenticationViewModel;

export function getAuthentication(
	id: number,
	signal?: AbortSignal
): Promise<IntegrationFlowAuthenticationViewModel> {
	return environmentHttpClient.get<IntegrationFlowAuthenticationViewModel>(
		BASE_URL + `/${id}`,
		undefined,
		{ signal }
	);
}

export type NewIntegrationFlowAuthenticationViewModel = {
	oAuth2RedirectUrl: string;
};

export function getNewAuthentication(
	signal?: AbortSignal
): Promise<NewIntegrationFlowAuthenticationViewModel> {
	return environmentHttpClient.get<NewIntegrationFlowAuthenticationViewModel>(
		`${BASE_URL}/New`,
		undefined,
		{ signal }
	);
}

export type StringSaveModel = {
	kind: StringSecretDiscriminator.String;
	string: string;
};

export type SecretSaveModel = {
	kind: StringSecretDiscriminator.Secret;
	secret: SecretValue;
};

export type StringOrSecretSaveModel =
	| StringSaveModel
	| SecretSaveModel;

export type KeyedValueSaveModel = {
	id: Guid | null;
	key: string;
	value: StringOrSecretSaveModel;
};

export type IntegrationFlowApiKeyAuthenticationSettings = {
	headers: readonly KeyedValueSaveModel[];
	queryParameters: readonly KeyedValueSaveModel[];
};

export type IntegrationFlowOAuth2AuthenticationSettings = {
	authorizationUrl: string;
	tokenUrl: string;
	clientId: string;
	clientSecret: SecretValue;
	scopes: readonly string[];
};

export type IntegrationFlowClientCertificateAuthenticationSettings = {
	certificate: OptionalModel<Base64FileUriUploadModel>;
	password: SecretValue;
};

export type IntegrationFlowAfasAuthenticationSettings = {
	apiUrl: string;
	environmentKey: string;
	apiKey: SecretValue;
	oneTimePassword: OptionalModel<string>;
};

export type IntegrationFlowApiKeyAuthenticationSaveModel = BaseIntegrationFlowAuthenticationSaveModel & {
	kind: IntegrationFlowAuthenticationKind.ApiKey;
	apiKey: IntegrationFlowApiKeyAuthenticationSettings;
};

export type IntegrationFlowOAuth2AuthenticationSaveModel = BaseIntegrationFlowAuthenticationSaveModel & {
	kind: IntegrationFlowAuthenticationKind.OAuth2;
	oAuth2: IntegrationFlowOAuth2AuthenticationSettings;
};

export type IntegrationFlowClientCertificateAuthenticationSaveModel = BaseIntegrationFlowAuthenticationSaveModel & {
	kind: IntegrationFlowAuthenticationKind.ClientCertificate;
	clientCertificate: IntegrationFlowClientCertificateAuthenticationSettings;
};

export type IntegrationFlowBasicAuthenticationSaveModel = BaseIntegrationFlowAuthenticationSaveModel & {
	kind: IntegrationFlowAuthenticationKind.Basic;
};

export type IntegrationFlowAfasAuthenticationSaveModel = BaseIntegrationFlowAuthenticationSaveModel & {
	kind: IntegrationFlowAuthenticationKind.Afas;
	afas: IntegrationFlowAfasAuthenticationSettings;
};

type BaseIntegrationFlowAuthenticationSaveModel = {
	name: string;
	allowedHosts: OptionalModel<readonly string[]>;
};

export type IntegrationFlowAuthenticationSaveModel =
	| IntegrationFlowApiKeyAuthenticationSaveModel
	| IntegrationFlowOAuth2AuthenticationSaveModel
	| IntegrationFlowBasicAuthenticationSaveModel
	| IntegrationFlowClientCertificateAuthenticationSaveModel
	| IntegrationFlowAfasAuthenticationSaveModel;

export enum IntegrationFlowAuthenticationErrorKind {
	AuthenticationIsRequired = 1,
	EmailAddressIsRequired = 2,
	IntegrationError = 3
};

export type OAuth2AuthenticationRequiredResult = {
	errorKind: IntegrationFlowAuthenticationErrorKind.AuthenticationIsRequired;
	authenticationKind: IntegrationFlowAuthenticationKind.OAuth2;
	redirectUrl: string;
};

export type AfasAuthenticationRequiredResult = {
	errorKind: IntegrationFlowAuthenticationErrorKind.AuthenticationIsRequired;
	authenticationKind: IntegrationFlowAuthenticationKind.Afas;
};

export type AfasEmailAddressIsRequiredResult = {
	errorKind: IntegrationFlowAuthenticationErrorKind.EmailAddressIsRequired;
	authenticationKind: IntegrationFlowAuthenticationKind.Afas;
};

export type AfasIntegrationErrorResult = {
	errorKind: IntegrationFlowAuthenticationErrorKind.IntegrationError;
	authenticationKind: IntegrationFlowAuthenticationKind.Afas;
};

export type IErrorSaveIntegrationFlowAuthenticationResult =
	| OAuth2AuthenticationRequiredResult
	| AfasAuthenticationRequiredResult
	| AfasEmailAddressIsRequiredResult
	| AfasIntegrationErrorResult;

export function createAuthentication(
	model: IntegrationFlowAuthenticationSaveModel,
	signal?: AbortSignal
): Promise<number> {
	return environmentHttpClient.post<number>(
		BASE_URL,
		model,
		{ signal }
	);
}

export function deleteAuthentications(
	ids: readonly number[],
	signal?: AbortSignal
): Promise<void> {
	return environmentHttpClient.delete<void>(
		`UiApi/IntegrationBuilder/Authentication`,
		ids,
		{ signal }
	);
}

export function updateAuthentication(
	id: number,
	model: IntegrationFlowAuthenticationSaveModel,
	signal?: AbortSignal
): Promise<number> {
	return environmentHttpClient.put<number>(
		BASE_URL + `/${id}`,
		model,
		{ signal }
	);
}

export enum IntegrationFlowAfasAuthenticationResultKind {
	RequestCreated = 0,
	RequireEmailAddress = 1
}

export type IntegrationFlowAfasAuthenticationResultModel = {
	kind: IntegrationFlowAfasAuthenticationResultKind;
};

export function requestAfasOneTimePassword(
	authenticationId: number,
	signal?: AbortSignal
): Promise<IntegrationFlowAfasAuthenticationResultModel> {
	return environmentHttpClient.post<IntegrationFlowAfasAuthenticationResultModel>(
		`Integrations/IntegrationFlow/AfasAuthenticate/${authenticationId}/Request`,
		undefined,
		{ signal }
	);
}

export type AfasRequestNewTokenModel = {
	oneTimePassword: string;
};

export function exchangeAfasOneTimePassword(
	authenticationId: number,
	model: AfasRequestNewTokenModel,
	signal?: AbortSignal
): Promise<void> {
	return environmentHttpClient.post<void>(
		`Integrations/IntegrationFlow/AfasAuthenticate/${authenticationId}/Exchange`,
		model,
		{ signal }
	);
}

export type IntegrationFlowMultipleAuthenticationsAreRequiredModel = {
	authentications: readonly IntegrationFlowAuthenticationIsRequiredModel[];
};

export type OAuth2IntegrationFlowAuthenticationIsRequiredModel = BaseIntegrationFlowAuthenticationIsRequiredModel & {
	kind: IntegrationFlowAuthenticationKind.OAuth2;
	authenticationUrl: string;
};

export type BasicIntegrationFlowAuthenticationIsRequiredModel = BaseIntegrationFlowAuthenticationIsRequiredModel & {
	kind: IntegrationFlowAuthenticationKind.Basic;
};

export type AfasIntegrationFlowAuthenticationIsRequiredModel = BaseIntegrationFlowAuthenticationIsRequiredModel & {
	kind: IntegrationFlowAuthenticationKind.Afas;
};

type BaseIntegrationFlowAuthenticationIsRequiredModel = {
	id: number;
	name: string;
};

export type IntegrationFlowAuthenticationIsRequiredModel =
	| OAuth2IntegrationFlowAuthenticationIsRequiredModel
	| BasicIntegrationFlowAuthenticationIsRequiredModel
	| AfasIntegrationFlowAuthenticationIsRequiredModel;